import { useQuery } from '@tanstack/react-query';
import { FileDown, Printer } from 'lucide-react';
import { api } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { Button, Card, ErrorState, SkeletonTable } from '@/features/common/ui';
import { DocumentView } from './DocumentView';
import { pdfUrl, type DocumentJson } from './document-layout';

/** The two documents a book issues. The path is the API's resource name. */
export type DocumentPath = 'invoices' | 'proformas';

/** The query key for one document, shared with the pages that invalidate it after Issue or Void. */
export const documentQueryKey = (path: DocumentPath, orgId: string | undefined, id: string) =>
  [path === 'invoices' ? 'invoice-document' : 'proforma-document', orgId, id] as const;

/**
 * What the settings blob carries for the letterhead. Declared here rather
 * than taken from `OrgSettings` so this screen reads the same keys whether
 * or not the validation package types them yet - see LetterheadCard.
 */
interface LetterheadSettings {
  letterhead?: { accentColour?: string | null; showLogo?: boolean | null } | null;
}

/**
 * The document on the page, with the two things a person does with it.
 *
 * ── DOWNLOAD PDF IS A LINK, WITH THE ORG IN ITS PATH ──
 * A new tab is a navigation, and a navigation cannot carry the `x-org-id`
 * header every fetch sends - docs/coding-standards.md records the failure:
 * a JSON error saved as a file. The PDF route accepts the org in the path
 * for exactly this, and the server sends the PDF `inline` under the
 * document's number, so the tab shows the invoice with the browser's own
 * save button. No fetch, no blob, no popup blocker.
 */
export function DocumentSection({ path, id }: { path: DocumentPath; id: string }) {
  const org = useActiveOrg();
  const settings = useOrgSettings();

  const query = useQuery({
    queryKey: documentQueryKey(path, org?.id, id),
    queryFn: () => api.get<DocumentJson>(`/${path}/${id}/document`),
    enabled: !!org?.id && !!id,
  });

  const letterhead = (settings.data as LetterheadSettings | undefined)?.letterhead;
  const accent = letterhead?.accentColour ?? org?.colour ?? null;
  const showLogo = letterhead?.showLogo !== false;
  const ready = !!query.data && !!org;

  return (
    <section aria-label="Document" className="space-y-3" data-testid="document-section">
      <div className="flex flex-wrap items-center justify-between gap-2 print:hidden">
        <h2 className="text-sm font-semibold">Document</h2>
        <div className="flex flex-wrap items-center gap-2">
          {ready ? (
            <Button variant="outline" size="sm" asChild>
              <a href={pdfUrl(path, org.id, id)} target="_blank" rel="noopener" data-testid="download-pdf">
                <FileDown className="size-4" aria-hidden />
                Download PDF
              </a>
            </Button>
          ) : (
            <Button variant="outline" size="sm" disabled>
              <FileDown className="size-4" aria-hidden />
              Download PDF
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => window.print()} disabled={!ready} data-testid="print-document">
            <Printer className="size-4" aria-hidden />
            Print
          </Button>
        </div>
      </div>

      {query.isLoading ? (
        <Card>
          <SkeletonTable rows={6} cols={4} />
        </Card>
      ) : query.isError || !query.data ? (
        <Card>
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        </Card>
      ) : (
        <DocumentView document={query.data} bookName={org?.name ?? ''} accent={accent} showLogo={showLogo} />
      )}
    </section>
  );
}
